import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CalendarClock,
  Code2,
  Loader2,
  MessageSquare,
  Play,
  Plus,
  Sparkles,
  Wand2,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ErrorState } from '@/components/error-state'
import {
  useInstallSkill,
  useRunScheduledSkill,
  useSkillCatalog,
  useToggleSkill,
  useUpdateScheduledSkill,
} from '@/features/skills/use-skills'
import type {
  BuiltinSkillDefinition,
  ScheduledSkillRun,
  SkillInstalled,
} from '@/features/skills/skills-api'

// M5-A: Interne Allowlist-Repo-Namen nicht mehr im UI anzeigen.
const CUSTOM_SCHEDULE_LABEL = 'Eigener Rhythmus (Erweitert)'

const SCHEDULE_OPTIONS = [
  { label: 'Täglich', cron: '0 6 * * *' },
  { label: 'Wöchentlich', cron: '0 6 * * 1' },
  { label: 'Monatlich', cron: '0 4 1 * *' },
  { label: CUSTOM_SCHEDULE_LABEL, cron: 'custom' },
]

export function AutomatisierungenPage() {
  const catalogQuery = useSkillCatalog()
  const catalog = catalogQuery.data
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border px-4 py-6 min-[761px]:px-12">
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-[26px] font-[650]">Automatisierungen</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Wiederkehrende Aufgaben: Recherche, Outputs, Analyse und geplante Skills.
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            GitHub-Skill
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-6 min-[761px]:px-12">
        <div className="mx-auto grid max-w-[1180px] gap-8">
          {catalogQuery.isLoading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : catalogQuery.isError ? (
            <ErrorState
              title="Automatisierungen konnten nicht geladen werden"
              description={
                catalogQuery.error instanceof Error
                  ? catalogQuery.error.message
                  : 'Bitte versuche es erneut.'
              }
              onRetry={() => void catalogQuery.refetch()}
              isRetrying={catalogQuery.isFetching}
            />
          ) : (
            <>
              <SectionTitle
                icon={<Wand2 className="h-4 w-4" />}
                title="Aufgaben"
                description="Wähle eine Aufgabe – Slash-Commands funktionieren weiterhin im Chat."
              />
              <BuiltinSkillsGrid skills={catalog?.built_in ?? []} />

              <SectionTitle
                icon={<CalendarClock className="h-4 w-4" />}
                title="Geplante Skills"
                description="Automatisch laufende Skills mit manuellem Start und Vorschau der nächsten Ausführung."
              />
              {(catalog?.scheduled ?? []).length === 0 ? (
                <EmptyScheduledState />
              ) : (
                <div className="grid gap-3">
                  {catalog?.scheduled.map((item) => (
                    <ScheduledSkillCard
                      key={item.run.id}
                      run={item.run}
                      definition={item.definition}
                    />
                  ))}
                </div>
              )}

              <SectionTitle
                icon={<Code2 className="h-4 w-4" />}
                title="Eigene Skills"
                description="Ergänzende Skills aus freigegebenen GitHub-Repositories."
              />
              {(catalog?.custom ?? []).length === 0 ? (
                <EmptyCustomState onInstall={() => setDialogOpen(true)} />
              ) : (
                <div className="grid gap-3">
                  {catalog?.custom.map((skill) => (
                    <CustomSkillCard key={skill.id} skill={skill} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <InstallDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  )
}

function SectionTitle({
  icon,
  title,
  description,
}: {
  icon: ReactNode
  title: string
  description: string
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </div>
      <div>
        <h2 className="font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}

const TASK_GROUP_LABELS: Record<string, string> = {
  research: 'Recherche',
  presentation: 'Präsentation',
  social: 'Social Media',
  content: 'Text & Content',
  data: 'Daten & Analyse',
  image: 'Bild',
  knowledge: 'Wissen',
}

function skillTaskGroup(skill: BuiltinSkillDefinition): string {
  if (!skill.taskGroup) return 'Sonstiges'
  return TASK_GROUP_LABELS[skill.taskGroup] ?? skill.taskGroup
}

function BuiltinSkillsGrid({ skills }: { skills: BuiltinSkillDefinition[] }) {
  const grouped = useMemo(() => {
    const map = new Map<string, BuiltinSkillDefinition[]>()
    for (const skill of skills) {
      const group = skillTaskGroup(skill)
      if (!map.has(group)) map.set(group, [])
      map.get(group)!.push(skill)
    }
    return map
  }, [skills])

  return (
    <div className="grid gap-6">
      {Array.from(grouped.entries()).map(([group, groupSkills]) => (
        <div key={group}>
          <h3 className="mb-2 text-sm font-semibold text-foreground">{group}</h3>
          <div className="grid gap-3 md:grid-cols-2">
            {groupSkills.map((skill) => (
              <BuiltinSkillCard key={skill.name} skill={skill} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function BuiltinSkillCard({ skill }: { skill: BuiltinSkillDefinition }) {
  const navigate = useNavigate()
  const example = skill.examples?.[0] ?? ''
  const command = example.startsWith('/') ? example : `/${skill.name} `
  const prefill = encodeURIComponent(command)

  return (
    <Card className="flex flex-col">
      <CardHeader className="space-y-2 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">/{skill.name}</CardTitle>
            <CardDescription className="mt-1 line-clamp-2">{skill.description}</CardDescription>
          </div>
          <Badge variant="secondary">Standard</Badge>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between gap-3 text-sm">
        {skill.examples?.[0] && (
          <div className="rounded-md bg-muted px-3 py-2 font-mono text-[12px] text-muted-foreground">
            {skill.examples[0]}
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          className="mt-auto w-full"
          onClick={() => navigate(`/fragen?prefill=${prefill}`)}
        >
          <MessageSquare className="mr-2 h-4 w-4" />
          Im Chat öffnen
        </Button>
      </CardContent>
    </Card>
  )
}

const SCHEDULE_STATUS_META: Record<string, { label: string; variant: 'outline' | 'secondary' | 'destructive' }> = {
  never: { label: 'Konfiguriert', variant: 'outline' },
  running: { label: 'Läuft', variant: 'secondary' },
  success: { label: 'Abgeschlossen', variant: 'outline' },
  error: { label: 'Fehlgeschlagen', variant: 'destructive' },
}

function ScheduleStatusBadge({ status, enabled }: { status: string; enabled: boolean }) {
  const meta = SCHEDULE_STATUS_META[status] ?? { label: status, variant: 'outline' as const }
  const label = !enabled && status !== 'error' ? `${meta.label} (pausiert)` : meta.label
  return <Badge variant={meta.variant}>{label}</Badge>
}

function ScheduledSkillCard({
  run,
  definition,
}: {
  run: ScheduledSkillRun
  definition: BuiltinSkillDefinition | null
}) {
  const update = useUpdateScheduledSkill()
  const runner = useRunScheduledSkill()
  const [mode, setMode] = useState(scheduleModeFor(run.schedule_cron))
  const [rawCron, setRawCron] = useState(run.schedule_cron)
  const isBusy = update.isPending || runner.isPending

  const saveSchedule = async (nextMode: string, nextCron = rawCron) => {
    const option = SCHEDULE_OPTIONS.find((item) => item.label === nextMode)
    const cron = option && option.cron !== 'custom' ? option.cron : nextCron.trim()
    if (!cron) return
    try {
      await update.mutateAsync({
        id: run.id,
        patch: {
          schedule_cron: cron,
          schedule_label: option?.label ?? CUSTOM_SCHEDULE_LABEL,
        },
      })
      setRawCron(cron)
      toast.success('Schedule gespeichert.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Schedule konnte nicht gespeichert werden')
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="min-w-0">
          <CardTitle className="font-mono text-base">/{run.skill_name}</CardTitle>
          <CardDescription className="mt-1">
            {definition?.description ?? run.description ?? 'Geplanter Skill'}
          </CardDescription>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ScheduleStatusBadge status={run.last_status} enabled={run.enabled} />
          <ToggleSwitch
            enabled={run.enabled}
            loading={isBusy}
            ariaLabel={`Schedule für /${run.skill_name} ${run.enabled ? 'pausieren' : 'aktivieren'}`}
            onToggle={(enabled) => {
              update.mutate(
                {
                  id: run.id,
                  patch: {
                    enabled,
                  },
                },
                {
                  onSuccess: () => toast.success(enabled ? 'Schedule aktiviert.' : 'Schedule pausiert.'),
                  onError: (err) => toast.error(err instanceof Error ? err.message : 'Update fehlgeschlagen'),
                },
              )
            }}
          />
        </div>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-[220px_1fr_auto] md:items-end">
        <div className="space-y-1.5">
          <Label htmlFor={`schedule-mode-${run.id}`}>Rhythmus</Label>
          <Select
            value={mode}
            onValueChange={(next) => {
              setMode(next)
              void saveSchedule(next)
            }}
            disabled={isBusy}
          >
            <SelectTrigger id={`schedule-mode-${run.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCHEDULE_OPTIONS.map((option) => (
                <SelectItem key={option.label} value={option.label}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          {mode === CUSTOM_SCHEDULE_LABEL && (
            <>
              <Label htmlFor={`cron-${run.id}`}>Cron-Ausdruck (Erweitert)</Label>
              <Input
                id={`cron-${run.id}`}
                value={rawCron}
                disabled={isBusy}
                onChange={(e) => {
                  setRawCron(e.target.value)
                  setMode(CUSTOM_SCHEDULE_LABEL)
                }}
                onBlur={() => void saveSchedule(CUSTOM_SCHEDULE_LABEL, rawCron)}
                className="font-mono"
              />
            </>
          )}
          <div className="text-[12px] text-muted-foreground">
            Nächste Ausführung: {run.next_run_at ? formatDateTime(run.next_run_at) : 'Vorschau für eigenen Rhythmus nicht verfügbar'}
          </div>
        </div>
        <Button
          variant="outline"
          disabled={isBusy}
          onClick={() => {
            runner.mutate(run.id, {
              onSuccess: () => toast.success('Geplanter Skill ausgeführt.'),
              onError: (err) => toast.error(err instanceof Error ? err.message : 'Ausführung fehlgeschlagen'),
            })
          }}
        >
          {runner.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Play className="mr-2 h-4 w-4" />
          )}
          Jetzt ausführen
        </Button>
        {run.last_error_message && (
          <div className="md:col-span-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {run.last_error_message}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function CustomSkillCard({ skill }: { skill: SkillInstalled }) {
  const navigate = useNavigate()
  const toggle = useToggleSkill()
  const dateFormatter = new Intl.DateTimeFormat('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md bg-accent/10 text-accent">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <CardTitle className="font-mono text-base">/{skill.skill_name}</CardTitle>
            <CardDescription className="mt-1">
              {skill.description ?? 'Keine Beschreibung'}
            </CardDescription>
          </div>
        </div>
        <ToggleSwitch
          enabled={skill.enabled}
          loading={toggle.isPending}
          ariaLabel={`Skill /${skill.skill_name} ${skill.enabled ? 'deaktivieren' : 'aktivieren'}`}
          onToggle={(enabled) => {
            toggle.mutate({ id: skill.id, enabled })
          }}
        />
      </CardHeader>
      <CardContent className="grid flex-1 grid-cols-[120px_1fr] gap-x-4 gap-y-1.5 text-sm">
        <span className="text-muted-foreground">Quelle</span>
        <span className="break-all font-mono text-[13px]">{skill.source_repo}</span>
        <span className="text-muted-foreground">Version</span>
        <span className="font-mono text-[13px]">{skill.version}</span>
        <span className="text-muted-foreground">Installiert</span>
        <span>{dateFormatter.format(new Date(skill.installed_at))}</span>
        <div className="col-span-2 mt-auto pt-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={!skill.enabled}
            onClick={() => navigate(`/fragen?prefill=${encodeURIComponent(`/${skill.skill_name} `)}`)}
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            Ausführen
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function EmptyScheduledState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed px-8 py-12 text-center">
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <CalendarClock className="h-5 w-5" />
      </div>
      <h2 className="mt-4 text-base font-semibold">Noch keine geplanten Skills</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Geplante Skills erscheinen automatisch, sobald sie für deinen Workspace aktiviert werden.
      </p>
    </div>
  )
}

function EmptyCustomState({ onInstall }: { onInstall: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed px-8 py-12 text-center">
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Sparkles className="h-5 w-5" />
      </div>
      <h2 className="mt-4 text-base font-semibold">Noch keine eigenen Skills installiert</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Standard-Skills sind sofort aktiv. Eigene Skills kommen über freigegebene
        GitHub-Repositories.
      </p>
      <Button className="mt-5" variant="outline" onClick={onInstall}>
        <Plus className="mr-2 h-4 w-4" />
        GitHub-Skill installieren
      </Button>
    </div>
  )
}

function ToggleSwitch({
  enabled,
  loading,
  onToggle,
  ariaLabel,
}: {
  enabled: boolean
  loading: boolean
  onToggle: (enabled: boolean) => void
  ariaLabel?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={ariaLabel}
      disabled={loading}
      onClick={() => onToggle(!enabled)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        enabled ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 translate-x-0.5 translate-y-0.5 rounded-full bg-background shadow-sm transition-transform ${
          enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  )
}

function InstallDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const install = useInstallSkill()
  const [url, setUrl] = useState('')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = url.trim()
    if (!trimmed) return
    try {
      const result = await install.mutateAsync({ source_repo: trimmed })
      toast.success(`Skill "${result.skill_name}" installiert.`)
      setUrl('')
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Installation fehlgeschlagen'
      toast.error(message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Skill aus GitHub installieren</DialogTitle>
          <DialogDescription>
            Aktuell sind nur freigegebene Repositories erlaubt. Standard-Skills sind bereits
            ohne Installation verfügbar.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="repo-url">GitHub-URL</Label>
            <Input
              id="repo-url"
              type="url"
              required
              placeholder="https://github.com/organisation/skill-repo"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <div className="text-[12px] text-muted-foreground">
              Welche Repositories freigegeben sind, erfährst du von der Administration.
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={install.isPending}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={install.isPending || !url.trim()}>
              {install.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Installieren
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function scheduleModeFor(cron: string): string {
  return SCHEDULE_OPTIONS.find((option) => option.cron === cron)?.label ?? CUSTOM_SCHEDULE_LABEL
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('de-CH', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}
